import React, { useState } from "react";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";
import { useRangeQuery } from "@/lib/useData";
import { useDateRange } from "@/lib/dateRange";
import { api } from "@/lib/api";
import {
  PageTitle, Card, Takeaway, LoadingBlocks, ErrorState, Forbidden,
} from "@/components/shared/primitives";
import { money } from "@/lib/format";
import { Tabs } from "@/components/kit";
import { Button } from "@/components/ui/button";

const REPORTS = [
  { type: "renewal", label: "Renewal report", note: "Per tool: usage, cost and a renew / review recommendation, soonest renewals first." },
  { type: "portfolio", label: "Monthly portfolio summary", note: "The whole AI spend on one page: totals and every tool's status." },
  { type: "team_adoption", label: "Team adoption report", note: "How each practice group has taken AI on board." },
];

function prettyHeader(h) {
  return h.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

const isMoneyHeader = (h) => /cost|spend|budget/.test(h);
const isPctHeader = (h) => /utilisation|adoption/.test(h);

function fmtCell(h, v) {
  if (v === null || v === undefined || v === "") return "-";
  if (typeof v === "number" && isMoneyHeader(h)) return money(v);
  if (typeof v === "number" && isPctHeader(h)) return `${Math.round(v * 100)}%`;
  if (typeof v === "number" && v < 1 && v > 0) return `${Math.round(v * 100)}%`;
  return String(v);
}

export default function Reports() {
  const [type, setType] = useState("renewal");
  const { range } = useDateRange();
  const { data, isLoading, error, refetch } = useRangeQuery(`report-${type}`, `/reports/${type}`);
  if (error?.code === "forbidden") return <Forbidden who="Admins, IT Admins and Team Leads" />;

  const download = async (format) => {
    try {
      const res = await api.raw.get(`/reports/${type}`, {
        params: { format, from: range.from, to: range.to },
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${type}.${format}`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success(`${format.toUpperCase()} downloaded.`);
    } catch (e) {
      toast.error("Export failed. Please try again.");
    }
  };

  const report = data?.data;
  const rows = report?.rows || [];
  const headers = rows[0] ? Object.keys(rows[0]) : [];

  return (
    <div>
      <PageTitle sub="Three ready-made reports, previewed here and exported to PDF or CSV." testid="reports-page">
        Reports
      </PageTitle>

      <div className="mb-6">
        <Tabs tabs={REPORTS.map((r) => ({ key: r.type, label: r.label }))} value={type} onChange={setType} testid="report-tabs" />
      </div>

      <Card className="p-5">
        <div className="flex items-start justify-between gap-4 mb-4">
          <div>
            <h2 className="text-[20px] font-semibold text-nb-ink">{report?.title || REPORTS.find((r) => r.type === type).label}</h2>
            <Takeaway>{REPORTS.find((r) => r.type === type).note}</Takeaway>
            <p className="text-[12px] text-nb-ink2 mt-1">Period: last 30 days · Generated {new Date().toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" })}</p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button data-testid="export-csv" variant="outline" className="rounded-[10px] gap-2" onClick={() => download("csv")}>
              <Download size={15} strokeWidth={1.5} /> CSV
            </Button>
            <Button data-testid="export-pdf" className="rounded-[10px] gap-2 bg-nb-navy hover:opacity-90 text-nb-cream font-semibold" onClick={() => download("pdf")}>
              <FileText size={15} strokeWidth={1.5} /> PDF
            </Button>
          </div>
        </div>

        {report?.totals && (
          <div className="flex flex-wrap gap-6 mb-4 pb-4 border-b" style={{ borderColor: "var(--border-subtle)" }}>
            <div><div className="nb-label mb-1">Monthly spend</div><div className="font-display text-[20px] font-bold text-nb-ink tnum">{money(report.totals.monthly_spend)}</div></div>
            <div><div className="nb-label mb-1">Seats</div><div className="font-display text-[20px] font-bold text-nb-ink tnum">{report.totals.seats}</div></div>
            <div><div className="nb-label mb-1">Active users</div><div className="font-display text-[20px] font-bold text-nb-ink tnum">{report.totals.active_users}</div></div>
          </div>
        )}

        {isLoading && <LoadingBlocks rows={4} />}
        {error && error.code !== "forbidden" && <ErrorState error={error} onRetry={refetch} />}
        {report && (
          <div className="overflow-x-auto" data-testid="report-preview">
            <table className="w-full text-[14px]">
              <thead>
                <tr className="nb-label text-left" style={{ background: "var(--bg-sunken)" }}>
                  {headers.map((h) => <th key={h} className="py-2.5 px-3 whitespace-nowrap first:rounded-l-md last:rounded-r-md">{prettyHeader(h)}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, i) => (
                  <tr key={i} className="border-b last:border-0 transition-colors hover:bg-[var(--bg-selected)]" style={{ borderColor: "var(--border-subtle)" }}>
                    {headers.map((h) => <td key={h} className={`py-3 px-3 text-nb-ink whitespace-nowrap ${typeof row[h] === "number" ? "tnum text-right" : ""}`}>{fmtCell(h, row[h])}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
